import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';
import { useFeatureFlags } from '@robinpowered/dashboard-apps-common';
import { useGetUserPermissionsForDashboardShellQuery } from '../generated/graphql';

export type UseUserPermissionsData = {
  canManageAmenities: boolean;
  canViewAnalytics: boolean;
  canViewBilling: boolean;
  canViewCheckpoints: boolean;
  canManageDevices: boolean;
  canViewDevices: boolean;
  canManageOfficeAccess: boolean;
  canViewOfficeAccess: boolean;
  canManageGroups: boolean;
  canManagePlaces: boolean;
  canManageRoles: boolean;
  canApproveSpaceRequests: boolean;
  canListUsers: boolean;
  canManageUsers: boolean;
  canViewVisitTab: boolean;
  canViewDeliveriesTab: boolean;
  canViewWorkweek: boolean;
  canManageWorkplaceServices: boolean;
  canManageWorkPolicies: boolean;
  canManageScenarioPlanning: boolean;
  arePersonalCalendarsEnabled: boolean;
  isInsightsFeatureEnabled: boolean;
  hasPendingApprovalNotifications: boolean;
};

export const DEFAULT_PERMISSIONS = {
  canManageAmenities: false,
  canViewAnalytics: false,
  canViewBilling: false,
  canViewCheckpoints: false,
  canManageDevices: false,
  canViewDevices: false,
  canManageOfficeAccess: false,
  canViewOfficeAccess: false,
  canManageGroups: false,
  canManagePlaces: false,
  canManageScenarioPlanning: false,
  canManageRoles: false,
  canApproveSpaceRequests: false,
  canListUsers: false,
  canManageUsers: false,
  canViewVisitTab: false,
  canViewDeliveriesTab: false,
  canViewWorkweek: false,
  canManageWorkplaceServices: false,
  canManageWorkPolicies: false,
  arePersonalCalendarsEnabled: false,
  isInsightsFeatureEnabled: false,
  hasPendingApprovalNotifications: false,
} satisfies UseUserPermissionsData;

type UseUserPermissionsState = {
  error?: ApolloError;
  isLoading: boolean;
};

export type UseUserPermissions = UseUserPermissionsState &
  UseUserPermissionsData;

export const useUserPermissions = (
  activeOrgId: string | null,
  hasTenant: boolean
): UseUserPermissions => {
  const { called, data, loading, error } =
    useGetUserPermissionsForDashboardShellQuery({
      skip: !activeOrgId || !hasTenant,
      nextFetchPolicy: 'cache-and-network',
    });

  const { checkFlag } = useFeatureFlags();
  const isBuildingDraftsEnabled = checkFlag('tanuki(building-drafts)', false);

  const permissions = useMemo(() => {
    const { getCurrentUserAndOrgs } = data || {};

    const orgFeatures =
      getCurrentUserAndOrgs?.organizationForCurrentTenant?.features;

    const isInsightsFeatureEnabled = orgFeatures?.insights ?? false;
    const arePersonalCalendarsDisabled = orgFeatures?.noPersonalCalendars;

    return {
      canApproveSpaceRequests:
        !!getCurrentUserAndOrgs?.canSeeApprovalNotificationsForCurrentTenant,
      canListUsers:
        !!getCurrentUserAndOrgs?.hasListUserPermissionForCurrentTenant,
      canManageAmenities:
        !!getCurrentUserAndOrgs?.hasAmenitiesManageForCurrentTenant,
      canViewAnalytics:
        !!getCurrentUserAndOrgs?.hasAnalyticsPermissionForCurrentTenant,
      canViewBilling:
        !!getCurrentUserAndOrgs?.hasBillingPermissionForCurrentTenant,
      canManageDevices:
        !!getCurrentUserAndOrgs?.hasDevicesManageForCurrentTenant,
      canViewDevices: !!getCurrentUserAndOrgs?.hasDevicesViewForCurrentTenant,
      canManageOfficeAccess:
        !!getCurrentUserAndOrgs?.hasOfficeAccessManagePermissionForCurrentTenant,
      canViewOfficeAccess:
        !!getCurrentUserAndOrgs?.hasOfficeAccessViewPermissionForCurrentTenant,
      canManageGroups: !!getCurrentUserAndOrgs?.hasGroupsManageForCurrentTenant,
      canManagePlaces: !!getCurrentUserAndOrgs?.hasPlacesManageForCurrentTenant,
      canManageRoles:
        !!getCurrentUserAndOrgs?.hasRolesManagePermissionForCurrentTenant,
      canManageUsers:
        !!getCurrentUserAndOrgs?.hasManageUserPermissionForCurrentTenant,
      canViewVisitTab:
        !!getCurrentUserAndOrgs?.hasViewVisitsTabForCurrentTenant,
      canViewDeliveriesTab:
        !!getCurrentUserAndOrgs?.hasDeliveriesViewPermissionForCurrentTenant,
      canManageWorkplaceServices:
        !!getCurrentUserAndOrgs?.hasWorkplaceServicesManagePermissionForCurrentTenant,
      canViewCheckpoints:
        !!getCurrentUserAndOrgs?.hasCheckpointsViewPermissionForCurrentTenant,
      canViewWorkweek: !!getCurrentUserAndOrgs?.hasWorkweekViewForCurrentTenant,
      canManageWorkPolicies:
        !!getCurrentUserAndOrgs?.hasWorkPoliciesManagePermissionForCurrentTenant,
      arePersonalCalendarsEnabled: !arePersonalCalendarsDisabled,
      isInsightsFeatureEnabled,
      hasPendingApprovalNotifications:
        !!getCurrentUserAndOrgs?.hasPendingApprovalNotificationsForOrganization,
      canManageScenarioPlanning: isBuildingDraftsEnabled
        ? !!getCurrentUserAndOrgs?.hasScenarioPlanManagePermissionForCurrentTenant
        : !!getCurrentUserAndOrgs?.hasScenarioPlanningManagePermissionForCurrentTenant,
    };
  }, [data, isBuildingDraftsEnabled]);

  return {
    ...permissions,
    error,
    isLoading: loading || !called,
  };
};
